import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { useMayOpen } from '@/features/auth/access'
import { useAuth } from '@/features/auth/store'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import {
  exportProducts,
  flattenCategories,
  formatMoney,
  listCategories,
  listProducts,
  setProductsListed,
  type Product,
  type ProductFilters,
} from '@/features/inventory/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'discontinued', label: 'Discontinued' },
]

const columns: Column<Product>[] = [
  {
    key: 'code',
    header: 'Code',
    className: 'font-mono text-xs',
    sortValue: (product) => product.code,
    cell: (product) => (
      <Link to={`/products/${product.id}`} className="hover:underline">
        {product.code}
      </Link>
    ),
  },
  {
    key: 'name',
    header: 'Product',
    className: 'font-medium',
    sortValue: (product) => product.name,
    cell: (product) => (
      <Link to={`/products/${product.id}`} className="hover:underline">
        {product.name}
      </Link>
    ),
  },
  {
    key: 'category',
    header: 'Category',
    className: 'text-slate-500 dark:text-slate-400',
    sortValue: (product) => product.category_name ?? '',
    cell: (product) => product.category_name ?? '—',
  },
  {
    key: 'unit_of_measure',
    header: 'Unit',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (product) => product.unit_of_measure,
  },
  {
    key: 'price',
    header: 'Price',
    className: 'text-right tabular-nums',
    headerClassName: 'text-right',
    sortValue: (product) => Number(product.price),
    cell: (product) => formatMoney(product.price),
  },
  {
    key: 'cost',
    // Blank rather than zero for a seller: the API omits cost without update rights on the
    // catalog, and printing 0.00 there would read as "we get these free".
    header: 'Cost',
    className: 'text-right tabular-nums text-slate-500 dark:text-slate-400',
    headerClassName: 'text-right',
    sortValue: (product) => (product.cost === null ? null : Number(product.cost)),
    cell: (product) => (product.cost === null ? '—' : formatMoney(product.cost)),
  },
  {
    key: 'status',
    header: 'Status',
    cell: (product) =>
      product.status === 'active' ? (
        <span className="rounded bg-success/10 px-2 py-0.5 text-xs font-medium text-success">
          Active
        </span>
      ) : (
        <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {product.status}
        </span>
      ),
  },
  {
    key: 'listed_on_marketplace',
    header: 'Marketplace',
    sortValue: (product) => (product.listed_on_marketplace ? 0 : 1),
    cell: (product) =>
      product.listed_on_marketplace ? (
        <span className="rounded bg-brand-500/10 px-2 py-0.5 text-xs font-medium text-brand-600 dark:text-brand-400">
          Listed
        </span>
      ) : (
        <span className="text-slate-400">—</span>
      ),
  },
]

/**
 * The catalog, as a list that leads to the stock position rather than to an edit form.
 *
 * Search and the category filter go to the server, because the catalog is thousands of
 * lines and the table only ever holds a page of it. The free-text box in the table header
 * filters the page that is already on screen — the two are different questions and the
 * screen offers both rather than pretending one is the other.
 */
export function Products() {
  // Nobody who can read the catalog is short of `inventory:read` today, but the two grants
  // are independent in the matrix and nothing stops them drifting apart.
  const mayOpenValuation = useMayOpen('/stock/valuation')
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<ProductFilters>({ page: 1, status: 'active' })
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const [selected, setSelected] = useState<string[]>([])

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => listProducts(filters),
    retry: false,
  })
  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    retry: false,
    staleTime: 5 * 60_000,
  })

  const products = data?.data ?? []
  const meta = data?.meta
  const options = flattenCategories(categories.data ?? [])

  const setListed = useMutation({
    mutationFn: async ({ ids, listed }: { ids: string[]; listed: boolean }) => {
      // One request for the whole selection, in one transaction on the server. A loop of
      // PATCHes would leave a half-applied catalog behind on the first failure, and this
      // screen would have no way to say which half.
      const updated = await setProductsListed(ids, listed)
      return { count: updated.length, listed }
    },
    onSuccess: ({ count, listed }) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      setSelected([])
      toast.success(
        `${count} product${count === 1 ? '' : 's'} ${listed ? 'listed on' : 'removed from'} the marketplace.`,
      )
    },
    onError: (mutationError: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.error(
        mutationError instanceof ApiError
          ? mutationError.message
          : 'Could not reach the server. Nothing further was changed.',
      )
    },
  })

  const set = (patch: Partial<ProductFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} product${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {mayOpenValuation && (
            <Link to="/stock/valuation">
              <Button variant="secondary">Stock valuation</Button>
            </Link>
          )}
          {can('products', 'create') && (
            <Button onClick={() => navigate('/products/new')}>New product</Button>
          )}
        </div>
      </header>

      <Card>
        <CardHeader
          title="Catalog"
          description="Open a product to see where its stock sits and how it got there."
        />
        <DataTable
          rows={products}
          columns={columns}
          getRowId={(product) => product.id}
          caption="Products"
          isLoading={isLoading}
          error={error}
          emptyMessage="No products match these filters."
          selection={
            can('products', 'update')
              ? {
                  selected,
                  onChange: setSelected,
                  actions: (ids) => (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={setListed.isPending}
                        onClick={() => setListed.mutate({ ids, listed: true })}
                      >
                        List on marketplace
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        loading={setListed.isPending}
                        onClick={() => setListed.mutate({ ids, listed: false })}
                      >
                        Remove from marketplace
                      </Button>
                    </>
                  ),
                }
              : undefined
          }
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="product-search">
                Search the catalog
              </label>
              <input
                id="product-search"
                className={SELECT_CLASS}
                placeholder="Code or name…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') set({ q: search })
                }}
                onBlur={() => set({ q: search })}
              />

              <label className="sr-only" htmlFor="product-category">
                Category
              </label>
              <select
                id="product-category"
                className={SELECT_CLASS}
                value={filters.category_id ?? ''}
                onChange={(event) => set({ category_id: event.target.value })}
              >
                <option value="">Any category</option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="product-status">
                Status
              </label>
              <select
                id="product-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value })}
              >
                <option value="">Any status</option>
                {STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>

              {(filters.q || filters.category_id || filters.status !== 'active') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setFilters({ page: 1, status: 'active' })
                  }}
                >
                  Clear
                </Button>
              )}

              <ExportButton
                filename="products"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportProducts(filters, format)}
                errorMessage="Could not export the catalog."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

    </div>
  )
}
